import { useQuery } from "@apollo/client/react";
import { Drawer, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { ChangeEvent, useEffect, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DownloadFilters } from "../../components/DownloadsDataGrid/DownloadFilters";
import { DownloadsDataGrid } from "../../components/DownloadsDataGrid/DownloadsDataGrid";
import {
  DownloadFiltersParamNames,
  getDefaultDownloadFilters,
  getDownloadsDatagridQueryVariables,
  getDownloadsNodes,
  IDownloadFilters,
} from "../../components/DownloadsDataGrid/downloadsDatagridUtils";
import { DrawerContainer } from "../../components/ui/common";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import {
  DownloadBundle,
  DownloadPart,
  PublishedSourceBundlesDocument,
  PublishedSourceBundlesQuery,
  PublishedSourceBundlesQueryVariables,
  UploadDownloadsDocument,
  UploadDownloadsQuery,
  UploadDownloadsQueryVariables,
} from "../../graphql/types";
import { isDateWithinRange, setOrRemoveRangeParam, setOrRemoveSearchParam, toDayDate } from "../../lib/Util";

const initialVisibilityModelDownloadsForUploadDatagrid = {
  [DataGridColumns.ReportDateTime]: true,
  [DataGridColumns.Started]: true,
  [DataGridColumns.Project]: true,
  [DataGridColumns.Name]: true,
  [DataGridColumns.Purpose]: true,
  [DataGridColumns.Status]: true,
  [DataGridColumns.Actions]: true,
  [DataGridColumns.Description]: false,
  [DataGridColumns.Created]: false,
  [DataGridColumns.Finished]: false,
  [DataGridColumns.DownloadDuration]: false,
  [DataGridColumns.QueuedDuration]: false,
};

const paramNames: DownloadFiltersParamNames = {
  projects: "drawerProjects",
  importNames: "drawerDownloadNames",
  bundleNames: "drawerBundleNames",
  purpose: "drawerPurpose",
  search: "drawerSearch",
  status: "drawerStatus",
  reportRange: {
    start: "drawerStart",
    end: "drawerEnd",
  },
  downloadedRange: {
    start: "drawerDownloaded_start",
    end: "drawerDownloaded_end",
  },
  sortBy: "drawerSortBy",
  sortDirection: "drawerSortDirection",
};

const updateSearchParams = (
  searchParams: URLSearchParams,
  filter: IDownloadFilters,
  setSearchParams: SetURLSearchParams
) => {
  const {
    projects,
    importNames,
    bundleNames,
    purpose,
    search,
    status,
    reportRange,
    downloadedRange,
    sortBy,

    sortDirection,
  } = filter;
  const updatedParams = new URLSearchParams(searchParams.toString());
  setOrRemoveSearchParam(paramNames.projects, projects?.join(","), updatedParams);
  setOrRemoveSearchParam(paramNames.importNames, importNames?.join(","), updatedParams);
  setOrRemoveSearchParam(paramNames.bundleNames, bundleNames?.join(","), updatedParams);
  setOrRemoveSearchParam(paramNames.purpose, purpose, updatedParams);
  setOrRemoveSearchParam(paramNames.search, search, updatedParams);
  setOrRemoveSearchParam(paramNames.status, status, updatedParams);
  setOrRemoveSearchParam(paramNames.sortDirection, sortDirection, updatedParams);
  setOrRemoveSearchParam(paramNames.sortBy, sortBy, updatedParams);
  setOrRemoveRangeParam(paramNames.reportRange.start, paramNames.reportRange.end, updatedParams, reportRange);
  setOrRemoveRangeParam(
    paramNames.downloadedRange.start,
    paramNames.downloadedRange.end,
    updatedParams,
    downloadedRange
  );
  if (updatedParams.toString() !== searchParams.toString()) setSearchParams(updatedParams);
};

const resetParams = (setSearchParams: SetURLSearchParams) => {
  setSearchParams((prevState) => {
    const updatedParams = new URLSearchParams(prevState.toString());
    updatedParams.delete(paramNames.projects);
    updatedParams.delete(paramNames.importNames);
    updatedParams.delete(paramNames.bundleNames);
    updatedParams.delete(paramNames.purpose);
    updatedParams.delete(paramNames.search);
    updatedParams.delete(paramNames.status);
    updatedParams.delete(paramNames.reportRange.start);
    updatedParams.delete(paramNames.reportRange.end);
    updatedParams.delete(paramNames.downloadedRange.start);
    updatedParams.delete(paramNames.downloadedRange.end);
    updatedParams.delete(paramNames.sortBy);
    updatedParams.delete(paramNames.sortDirection);
    return updatedParams;
  });
};

const getBundlesByParts = (parts: DownloadPart[] | undefined): DownloadBundle[] => {
  const bundleMap = new Map<string, { bundle: DownloadBundle }>();

  if (!parts) return [];

  parts.forEach((part) => {
    const bundleId = part.download.id;

    if (!bundleMap.has(bundleId)) {
      bundleMap.set(bundleId, { bundle: part.download });
    }

    const bundleEntry = bundleMap.get(bundleId);
    if (bundleEntry) {
      // Get rid of the Bundle within the part, as it is already in bundleMap
      // Passing it as {} because it is not used in any way by the datagrid, but cannot set to null or undefined
      const bundlelessPart = { ...part, download: {} as DownloadBundle };
      if (bundleEntry.bundle.parts) {
        bundleEntry.bundle.parts.push(bundlelessPart);
      } else {
        bundleEntry.bundle = { ...bundleEntry.bundle, parts: [bundlelessPart] };
      }
    }
  });

  return Array.from(bundleMap.values()).map(({ bundle }) => bundle);
};

const filterDownloads = (downloads: DownloadBundle[] | undefined, filter: IDownloadFilters): DownloadBundle[] => {
  if (!downloads) return [];
  return downloads.filter((download) => {
    if (filter.projects.length > 0 && !filter.projects.includes(download?.source_tables[0]?.project ?? ""))
      return false;
    if (filter.importNames.length > 0 && !filter.importNames.includes(download?.source_tables[0]?.name ?? ""))
      return false;
    if (
      (filter.bundleNames.length > 0 && !filter.bundleNames.includes(download.source_tables[0]?.name ?? "")) ||
      filter.bundleNames.some((bundleName) => download.source_tables[0]?.name.includes(`{${bundleName}.`))
    )
      return false;
    if (filter.purpose && download.purpose !== filter.purpose) return false;
    if (filter.search && !download.source_tables.some((table) => table?.name?.includes(filter.search))) return false;
    if (filter.status && download.status !== filter.status) return false;

    if (
      filter.downloadedRange.start &&
      filter.downloadedRange.end &&
      (!download.started ||
        !isDateWithinRange(new Date(download.started), filter.downloadedRange.start, filter.downloadedRange.end))
    ) {
      return false;
    }
    if (
      filter.reportRange.start &&
      filter.reportRange.end &&
      download.report_datetime &&
      !isDateWithinRange(toDayDate(download.report_datetime.iso), filter.reportRange.start, filter.reportRange.end)
    ) {
      return false;
    }

    return true;
  });
};

const DownloadsForUploadDrawer = ({ handleCloseDrawer }: { handleCloseDrawer: () => void }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const uploadId = searchParams.get("selectedUploadId") || undefined;
  const uploadName = searchParams.get("selectedUploadName") || undefined;

  const { data, loading, error } = useQuery<UploadDownloadsQuery, UploadDownloadsQueryVariables>(
    UploadDownloadsDocument,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        id: uploadId || "",
      },
    }
  );

  const theme = useTheme();

  const [filter, setFilter] = useState<IDownloadFilters>(
    getDefaultDownloadFilters(searchParams, false, undefined, paramNames)
  );

  useEffect(() => {
    updateSearchParams(searchParams, filter, setSearchParams);
  }, [filter, searchParams, setSearchParams]);

  const { user } = useAuth();

  const treeDataQuery = useQuery<PublishedSourceBundlesQuery, PublishedSourceBundlesQueryVariables>(
    PublishedSourceBundlesDocument
  );

  if (error) {
    toast.error("There was a problem processing the request. Contact support if the problem persist");
    handleCloseDrawer();
  }

  if (uploadId === undefined || uploadName === undefined) {
    toast.error("Page was accessed with the wrong URL params.");
    console.error("Downloads for uploads drawer was accessed with the wrong URL params.");
    handleCloseDrawer();
    return null;
  }

  const { selectedNodes, nonSelectedNodes } = getDownloadsNodes(treeDataQuery.data, filter);

  const downloadParts = data?.upload?.inputs as DownloadPart[];

  const mappedDownloads: DownloadBundle[] = getBundlesByParts(downloadParts);

  const filteredDownloads = filterDownloads(mappedDownloads, filter);

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={(event: ChangeEvent) => {
        event.stopPropagation();
        resetParams(setSearchParams);
        handleCloseDrawer();
      }}
      data-testid="downloads-for-upload-drawer"
    >
      <DrawerContainer>
        <Stack spacing="20px" sx={{ width: "100%" }}>
          <IconButton onClick={handleCloseDrawer} sx={{ alignSelf: "flex-end" }}>
            <XIcon width="24px" height="24px" color={theme.palette.text50.main} />
          </IconButton>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row">
              <Typography variant="heading.five">Downloads for upload {uploadName}</Typography>
            </Stack>
            <DownloadFilters
              loading={treeDataQuery.loading}
              filter={filter}
              setFilter={setFilter}
              queryVariables={getDownloadsDatagridQueryVariables(filter)}
              nonSelectedNodesInFilter={nonSelectedNodes}
              selectedNodesInFilter={selectedNodes}
              initialVisibilityModel={initialVisibilityModelDownloadsForUploadDatagrid}
              datagridPreferencesElement={user.datagridPreferences.downloadsForUpload}
              preferenceElementToUpdate="downloadsForUpload"
              hideTextSearch
            />
          </Stack>

          <DownloadsDataGrid
            datagridPreferencesElement={user.datagridPreferences.downloadsForUpload}
            filter={filter}
            loading={loading}
            onRowsScrollEnd={() => {}}
            rows={filteredDownloads || []}
            initialVisibilityModel={initialVisibilityModelDownloadsForUploadDatagrid}
            preferenceElementToUpdate="downloadsForUpload"
          />
        </Stack>
      </DrawerContainer>
    </Drawer>
  );
};
export { DownloadsForUploadDrawer };
